/**
 * Time off.
 *
 * Two screens in one file, because they are two ends of the same fact
 * and share every type between them.
 *
 * ---------------------------------------------------------------------
 * MY LEAVE — the balance, and the list behind it
 *
 * The balance is one large number, because that is the question. Under
 * it is the ledger, and the ledger is not a developer's audit trail —
 * it is the answer to "I thought I had four days left", and it is shown
 * to the person whose days they are without anybody having to ask a
 * manager. A balance nobody can see the working of is a balance people
 * argue about.
 *
 * ---------------------------------------------------------------------
 * THE MANAGER'S SCREEN LEADS WITH THE CLASHES, NOT THE QUEUE
 *
 * The requests waiting for a decision are the obvious thing to put at
 * the top, and they are the wrong thing. A request is a decision nobody
 * has made yet and can wait until this afternoon. A published shift on a
 * day somebody is already signed off to be away is a hole in next week's
 * roster that exists right now, was created by two people who both did
 * their job correctly, and will be discovered at six in the morning by
 * whoever turns up expecting cover.
 *
 * 0145 refuses to refuse that collision, for 0183's reason. So this
 * screen is where it has to be impossible to miss.
 */

import { useState } from 'react';
import { api } from '../lib/api.ts';
import { useApi, useMutation } from '../lib/useApi.ts';
import { date as fmtDate, titleCase } from '../lib/format.ts';
import { Card, Empty, ErrorBanner, Loading, Status, Tile } from '../components/ui.tsx';

interface Balance {
  staff_member_id: string;
  display_name: string;
  job_title: string;
  department: string;
  employment_type: string;
  leave_type_id: string;
  leave_type: string;
  leave_type_code: string;
  is_paid: boolean;
  accrued_minutes: number;
  taken_minutes: number;
  adjusted_minutes: number;
  balance_minutes: number;
  balance_hours: string;
  balance_days: string | null;
  booked_ahead_minutes: number;
}

interface Request {
  id: string;
  staff_member_id: string;
  display_name: string;
  department: string;
  leave_type: string;
  is_paid: boolean;
  starts_on: string;
  ends_on: string;
  calendar_days: number;
  minutes: number;
  hours: string;
  reason: string | null;
  status: string;
  decision_note: string | null;
  cancelled_reason: string | null;
  rostered_shifts: number;
}

interface Conflict {
  shift_id: string;
  work_date: string;
  starts_at: string;
  ends_at: string;
  job_title: string;
  department: string;
  request_id: string;
  display_name: string;
  leave_type: string;
  leave_from: string;
  leave_to: string;
}

interface LeaveType {
  id: string;
  code: string;
  name: string;
  is_paid: boolean;
  is_accrued: boolean;
  needs_reason: boolean;
}

const hhmm = (t: string) => t.slice(0, 5);
const days = (b: Balance) => (b.balance_days ? `${b.balance_days} days` : `${b.balance_hours}h`);

// =====================================================================
// Mine
// =====================================================================

export function MyLeavePage() {
  const balances = useApi<{ balances: Balance[] }>('/v1/leave/balances');
  const requests = useApi<{ requests: Request[] }>('/v1/leave/requests');
  const types = useApi<{ types: LeaveType[] }>('/v1/leave/types');
  const [asking, setAsking] = useState(false);

  if (balances.loading && !balances.data) return <Loading />;

  const mine = balances.data?.balances ?? [];
  const staffId = mine[0]?.staff_member_id;

  return (
    <>
      <h1>My Time Off</h1>
      <ErrorBanner error={balances.error ?? requests.error ?? types.error} />

      {mine.length === 0 ? (
        <Empty>
          You have no employment on file, so there is nothing to book leave against. A
          controller with a login and no job is an ordinary thing, not a fault.
        </Empty>
      ) : (
        <>
          <div className="tiles">
            {mine.map((b) => (
              <Tile
                key={b.leave_type_id}
                label={b.leave_type}
                value={days(b)}
                note={
                  b.booked_ahead_minutes > 0
                    ? `${(b.booked_ahead_minutes / 60).toFixed(1)}h already booked`
                    : undefined
                }
                tone={b.balance_minutes < 0 ? 'warning' : undefined}
              />
            ))}
          </div>

          <div className="actions">
            <button onClick={() => setAsking(!asking)}>
              {asking ? 'Never mind' : 'Ask for time off'}
            </button>
          </div>

          {asking && staffId && (
            <AskForLeave
              staffMemberId={staffId}
              types={types.data?.types ?? []}
              onAsked={() => {
                setAsking(false);
                requests.reload();
                balances.reload();
              }}
            />
          )}

          <Card title="What You Have Asked For">
            <MyRequests
              requests={requests.data?.requests ?? []}
              onChanged={() => {
                requests.reload();
                balances.reload();
              }}
            />
          </Card>

          {staffId && <Ledger staffMemberId={staffId} />}
        </>
      )}
    </>
  );
}

function MyRequests({
  requests,
  onChanged,
}: {
  requests: Request[];
  onChanged: () => void;
}) {
  const [cancelling, setCancelling] = useState<string | null>(null);
  const [reason, setReason] = useState('');

  const cancel = useMutation(async (id: string) => {
    const r = await api(`/v1/leave/requests/${id}/cancel`, {
      method: 'POST',
      body: { reason },
    });
    setCancelling(null);
    setReason('');
    onChanged();
    return r;
  });

  if (requests.length === 0) return <Empty>Nothing asked for yet.</Empty>;

  return (
    <>
      <ErrorBanner error={cancel.error} />
      <div className="table">
        <table className="data data--cards">
          <thead>
            <tr>
              <th>When</th>
              <th>Kind</th>
              <th className="num">Hours</th>
              <th>State</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {requests.map((r) => (
              <tr key={r.id} className={r.status === 'cancelled' ? 'muted' : undefined}>
                <td data-label="When" className="cell--title">
                  {fmtDate(r.starts_on)}
                  {r.ends_on !== r.starts_on && ` – ${fmtDate(r.ends_on)}`}
                </td>
                <td data-label="Kind" className="muted">
                  {r.leave_type}
                  {!r.is_paid && <span className="small"> · unpaid</span>}
                </td>
                <td data-label="Hours" className="num">
                  {r.hours}
                </td>
                <td data-label="State">
                  <Status
                    tone={
                      r.status === 'approved'
                        ? 'good'
                        : r.status === 'declined'
                          ? 'serious'
                          : r.status === 'cancelled'
                            ? 'neutral'
                            : 'warning'
                    }
                  >
                    {titleCase(r.status)}
                  </Status>
                  {r.decision_note && (
                    <span className="muted small"> · {r.decision_note}</span>
                  )}
                  {r.cancelled_reason && (
                    <span className="muted small"> · {r.cancelled_reason}</span>
                  )}
                </td>
                <td className="cell--action">
                  {(r.status === 'submitted' || r.status === 'approved') &&
                    (cancelling === r.id ? (
                      <>
                        <input
                          value={reason}
                          onChange={(e) => setReason(e.target.value)}
                          placeholder="Why"
                        />
                        <button
                          className="small"
                          disabled={!reason.trim() || cancel.busy}
                          onClick={() => cancel.run(r.id)}
                        >
                          Cancel it
                        </button>
                      </>
                    ) : (
                      <button className="linkish" onClick={() => setCancelling(r.id)}>
                        Cancel
                      </button>
                    ))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function AskForLeave({
  staffMemberId,
  types,
  onAsked,
}: {
  staffMemberId: string;
  types: LeaveType[];
  onAsked: () => void;
}) {
  const [form, setForm] = useState({
    leave_type_id: '',
    from: new Date().toISOString().slice(0, 10),
    to: new Date().toISOString().slice(0, 10),
    reason: '',
  });

  const chosen = types.find((t) => t.id === (form.leave_type_id || types[0]?.id));

  const ask = useMutation(async () => {
    const r = await api('/v1/leave/requests', {
      method: 'POST',
      body: {
        staff_member_id: staffMemberId,
        leave_type_id: form.leave_type_id || types[0]?.id,
        from: form.from,
        to: form.to,
        reason: form.reason.trim() || undefined,
      },
    });
    onAsked();
    return r;
  });

  return (
    <Card title="Ask for Time Off">
      <ErrorBanner error={ask.error} />
      <div className="grid-2">
        <label className="field">
          <span>Kind</span>
          <select
            value={form.leave_type_id || types[0]?.id || ''}
            onChange={(e) => setForm((f) => ({ ...f, leave_type_id: e.target.value }))}
          >
            {types.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
                {!t.is_paid ? ' (unpaid)' : ''}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>From</span>
          <input
            type="date"
            value={form.from}
            onChange={(e) =>
              setForm((f) => ({ ...f, from: e.target.value, to: f.to < e.target.value ? e.target.value : f.to }))
            }
          />
        </label>
        <label className="field">
          <span>Until</span>
          <input
            type="date"
            min={form.from}
            value={form.to}
            onChange={(e) => setForm((f) => ({ ...f, to: e.target.value }))}
          />
          <div className="hint">
            Counted as working days at your club's standard day. Ask your manager if a
            half-day is what you meant.
          </div>
        </label>
        <label className="field">
          <span>Why{chosen?.needs_reason ? '' : ' (optional)'}</span>
          <input
            value={form.reason}
            onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
          />
        </label>
      </div>
      <div className="actions">
        <button
          disabled={ask.busy || (chosen?.needs_reason && !form.reason.trim())}
          onClick={() => ask.run()}
        >
          {ask.busy ? 'Asking…' : 'Ask'}
        </button>
      </div>
    </Card>
  );
}

/**
 * The working behind the balance.
 *
 * Shown to the person whose days they are, without a manager in the way.
 * A balance nobody can see the working of is a balance people argue
 * about, and this whole module is a ledger rather than a number for
 * exactly that reason.
 */
function Ledger({ staffMemberId }: { staffMemberId: string }) {
  const [open, setOpen] = useState(false);
  const { data, error, loading } = useApi<{ entries: any[] }>(
    open ? `/v1/leave/ledger?staff_member_id=${staffMemberId}` : '',
    [open, staffMemberId],
  );

  if (!open) {
    return (
      <div className="actions">
        <button className="secondary small" onClick={() => setOpen(true)}>
          Where the balance came from
        </button>
      </div>
    );
  }

  if (loading && !data) return <Loading rows={2} />;

  return (
    <Card title="Where the Balance Came From">
      <ErrorBanner error={error} />
      <div className="table">
        <table className="data">
          <thead>
            <tr>
              <th>Date</th>
              <th>Kind</th>
              <th>What</th>
              <th className="num">Hours</th>
              <th>Why</th>
            </tr>
          </thead>
          <tbody>
            {(data?.entries ?? []).map((e) => (
              <tr key={e.id}>
                <td>{fmtDate(e.effective_on)}</td>
                <td className="muted">{e.leave_type}</td>
                <td className="muted">{titleCase(e.kind.replace('_', ' '))}</td>
                <td className="num">
                  {e.minutes > 0 ? '+' : ''}
                  {(e.minutes / 60).toFixed(2)}
                </td>
                <td className="muted small">
                  {e.reason ?? (e.worked_on ? `Worked ${fmtDate(e.worked_on)}` : '—')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="actions">
        <button className="secondary small" onClick={() => setOpen(false)}>
          Hide
        </button>
      </div>
    </Card>
  );
}

// =====================================================================
// The manager's screen
// =====================================================================

export function LeavePage() {
  const conflicts = useApi<{ conflicts: Conflict[] }>('/v1/leave/conflicts');
  const requests = useApi<{ requests: Request[]; may_decide: boolean }>('/v1/leave/requests');
  const balances = useApi<{ balances: Balance[] }>('/v1/leave/balances');

  const decide = useMutation(async (args: { id: string; decision: string; note?: string }) => {
    const r = await api(`/v1/leave/requests/${args.id}/decide`, {
      method: 'POST',
      body: { decision: args.decision, note: args.note },
    });
    requests.reload();
    balances.reload();
    conflicts.reload();
    return r;
  });

  if (requests.loading && !requests.data) return <Loading />;

  const clashes = conflicts.data?.conflicts ?? [];
  const all = requests.data?.requests ?? [];
  const waiting = all.filter((r) => r.status === 'submitted');
  const owed = (balances.data?.balances ?? []).reduce((n, b) => n + b.balance_minutes, 0);

  return (
    <>
      <h1>Leave</h1>
      <ErrorBanner error={requests.error ?? conflicts.error ?? decide.error} />

      <div className="tiles">
        <Tile
          label="Waiting on you"
          value={String(waiting.length)}
          tone={waiting.length > 0 ? 'warning' : 'good'}
        />
        <Tile
          label="Clashes with the roster"
          value={String(clashes.length)}
          tone={clashes.length > 0 ? 'serious' : 'good'}
          note={clashes.length > 0 ? 'somebody has to move one of them' : undefined}
        />
        <Tile
          label="Hours owed"
          value={(owed / 60).toFixed(0)}
          note="what the club would pay out today"
        />
      </div>

      {/* First, and above the queue. See the file header: a request can
          wait until this afternoon; a hole in next week's roster cannot,
          and nobody made a mistake to create it. */}
      {clashes.length > 0 && (
        <Card title={`${clashes.length} rostered on a day they are signed off`}>
          <p className="muted small">
            Nobody did anything wrong here. The roster is written weeks ahead and leave is
            approved separately, so this is the list where the two meet. Move the shift or
            cancel the leave — nothing will decide it for you.
          </p>
          <div className="table">
            <table className="data data--cards">
              <thead>
                <tr>
                  <th>Who</th>
                  <th>Rostered</th>
                  <th>Where</th>
                  <th>Away</th>
                </tr>
              </thead>
              <tbody>
                {clashes.map((c) => (
                  <tr key={c.shift_id}>
                    <td data-label="Who" className="cell--title">
                      {c.display_name}
                      <span className="muted small"> · {c.job_title}</span>
                    </td>
                    <td data-label="Rostered">
                      {fmtDate(c.work_date)}
                      <span className="muted small">
                        {' '}
                        {hhmm(c.starts_at)}–{hhmm(c.ends_at)}
                      </span>
                    </td>
                    <td data-label="Where" className="muted">
                      {c.department}
                    </td>
                    <td data-label="Away" className="muted">
                      {c.leave_type}, {fmtDate(c.leave_from)} – {fmtDate(c.leave_to)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Card title="Waiting for a Decision">
        {waiting.length === 0 ? (
          <Empty>Nothing waiting.</Empty>
        ) : (
          <div className="table">
            <table className="data data--cards">
              <thead>
                <tr>
                  <th>Who</th>
                  <th>When</th>
                  <th>Kind</th>
                  <th className="num">Hours</th>
                  <th>Roster</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {waiting.map((r) => (
                  <tr key={r.id}>
                    <td data-label="Who" className="cell--title">
                      {r.display_name}
                      <span className="muted small"> · {r.department}</span>
                    </td>
                    <td data-label="When">
                      {fmtDate(r.starts_on)}
                      {r.ends_on !== r.starts_on && ` – ${fmtDate(r.ends_on)}`}
                    </td>
                    <td data-label="Kind" className="muted">
                      {r.leave_type}
                      {r.reason && <span className="small"> · {r.reason}</span>}
                    </td>
                    <td data-label="Hours" className="num">
                      {r.hours}
                    </td>
                    <td data-label="Roster">
                      {r.rostered_shifts > 0 ? (
                        <Status tone="warning">
                          {r.rostered_shifts} shift{r.rostered_shifts === 1 ? '' : 's'}
                        </Status>
                      ) : (
                        <span className="muted">clear</span>
                      )}
                    </td>
                    <td className="cell--action">
                      <button
                        className="small"
                        disabled={decide.busy}
                        onClick={() => decide.run({ id: r.id, decision: 'approved' })}
                      >
                        Approve
                      </button>{' '}
                      <button
                        className="secondary small"
                        disabled={decide.busy}
                        onClick={() => decide.run({ id: r.id, decision: 'declined' })}
                      >
                        Decline
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card title="What Everybody Has">
        {(balances.data?.balances ?? []).length === 0 ? (
          <Empty>No balances yet. Accrue from the timesheets, or bring them across.</Empty>
        ) : (
          <div className="table">
            <table className="data data--cards">
              <thead>
                <tr>
                  <th>Who</th>
                  <th>Department</th>
                  <th>Kind</th>
                  <th className="num">Balance</th>
                  <th className="num">Booked</th>
                </tr>
              </thead>
              <tbody>
                {(balances.data?.balances ?? []).map((b) => (
                  <tr key={`${b.staff_member_id}-${b.leave_type_id}`}>
                    <td data-label="Who" className="cell--title">
                      {b.display_name}
                    </td>
                    <td data-label="Department" className="muted">
                      {b.department}
                    </td>
                    <td data-label="Kind" className="muted">
                      {b.leave_type}
                    </td>
                    <td data-label="Balance" className="num">
                      {days(b)}
                    </td>
                    <td data-label="Booked" className="num muted">
                      {b.booked_ahead_minutes > 0
                        ? `${(b.booked_ahead_minutes / 60).toFixed(1)}h`
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}
